import { useState } from "react";
import { toast } from "sonner";
import { BellRing, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function WaitlistForm({ product }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/waitlist", { product_id: product.product_id, ...form });
      if (data.status === "already") {
        toast.info(data.message);
      } else {
        toast.success(data.message);
      }
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="waitlist-done" className="rounded-2xl border border-sage/40 bg-sage-light/50 p-6 text-center">
        <BellRing size={26} className="mx-auto text-sage" />
        <p className="mt-3 font-serif text-xl font-semibold text-bark">Vous êtes sur la liste !</p>
        <p className="mt-1 text-sm text-bark-soft">Nous vous préviendrons dès le retour en stock de « {product.name} ».</p>
      </div>
    );
  }

  return (
    <form data-testid="waitlist-form" onSubmit={submit} className="space-y-4 rounded-2xl border border-honey/40 bg-amber-50/60 p-6">
      <div className="flex items-center gap-2">
        <BellRing size={20} className="text-amber-deep" />
        <p className="font-serif text-xl font-semibold text-bark">Prévenez-moi du retour</p>
      </div>
      <p className="text-sm leading-relaxed text-bark-soft">
        Ce produit est épuisé. Laissez vos coordonnées : nous vous contacterons en priorité dès la prochaine disponibilité.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <input
          data-testid="waitlist-name"
          value={form.name}
          onChange={update("name")}
          required
          placeholder="Votre nom"
          className="rounded-xl border border-amber-900/20 bg-white px-4 py-3 text-sm text-bark outline-none transition-colors focus:border-amber-deep"
        />
        <input
          data-testid="waitlist-email"
          type="email"
          value={form.email}
          onChange={update("email")}
          required
          placeholder="Votre e-mail"
          className="rounded-xl border border-amber-900/20 bg-white px-4 py-3 text-sm text-bark outline-none transition-colors focus:border-amber-deep"
        />
      </div>
      <input
        data-testid="waitlist-phone"
        value={form.phone}
        onChange={update("phone")}
        placeholder="Téléphone (facultatif)"
        className="w-full rounded-xl border border-amber-900/20 bg-white px-4 py-3 text-sm text-bark outline-none transition-colors focus:border-amber-deep"
      />
      <textarea
        data-testid="waitlist-message"
        value={form.message}
        onChange={update("message")}
        rows={2}
        placeholder="Un mot pour l'apiculteur (quantité souhaitée, précisions…)"
        className="w-full rounded-xl border border-amber-900/20 bg-white px-4 py-3 text-sm text-bark outline-none transition-colors focus:border-amber-deep"
      />
      <button
        data-testid="waitlist-submit"
        type="submit"
        disabled={loading}
        className="flex w-full items-center justify-center gap-2 rounded-full bg-amber-deep px-7 py-3.5 font-semibold text-white shadow-md shadow-amber-900/15 transition-all duration-200 hover:bg-honey active:scale-95 disabled:opacity-60"
      >
        {loading ? <Loader2 size={18} className="animate-spin" /> : <BellRing size={18} />}
        M'inscrire sur la liste d'attente
      </button>
    </form>
  );
}
